import { createApp } from 'vue'
import { createPinia } from 'pinia'
import VueDOMPurifyHTML from 'vue-dompurify-html'
import Vue3Toastify, { type ToastContainerOptions } from 'vue3-toastify'
import 'vue3-toastify/dist/index.css'
import { DotLottie } from '@lottiefiles/dotlottie-web'
import dotLottieWasmUrl from '@lottiefiles/dotlottie-web/dotlottie-player.wasm?url'

import App from './App.vue'
import './assets/main.css'
import { i18nInstance } from './translations/i18n'
import router from './routes/router'
import vRipple from './directives/vRipple'
import vFitTextWidth from './directives/vFitTextWidth'
import { vMaska } from 'maska/vue'
import * as ConfirmDialog from 'vuejs-confirm-dialog'
import { dragscroll } from 'vue-dragscroll'
import { logError, scopedLogger } from '@lib/utils/logUtils'

// dotlottie-web fetches its wasm engine from jsdelivr/unpkg by default (see Lottie.vue).
// Point it at the copy bundled with the app so it works fully offline (kiosk has no guaranteed internet).
DotLottie.setWasmUrl(dotLottieWasmUrl)

const log = scopedLogger('main.ts')

const describeError = (error: unknown) =>
  error instanceof Error ? `${error.message} ${error.stack ?? ''}` : String(error)

const pinia = createPinia()
const app = createApp(App)

// In production Vue swallows errors thrown in render/setup/hooks/watchers (console.error only),
// so they never reach electron-log's errorHandler and the console transport is disabled.
app.config.errorHandler = (error, instance, info) => {
  const component = instance?.$options.__name ?? instance?.$options.name ?? 'unknown'
  logError(log, `VUE_ERROR component={${component}} info={${info}} ${describeError(error)}`)
  console.error(error)
}

router.onError((error, to) => {
  logError(log, `ROUTER_ERROR to={${to.fullPath}} ${describeError(error)}`)
})

// directives
app.directive('ripple', vRipple)
app.directive('maska', vMaska)
app.directive('fitTextWidth', vFitTextWidth)
app.directive('dragscroll', dragscroll)

// plugins
app.use(Vue3Toastify, {
  // duration default
  autoClose: 5000,
  containerId: 'adk-toasts',
  position: 'bottom-center',
  // behaviour
  icon: false,
  hideProgressBar: true,
  closeButton: false,
  newestOnTop: true,
  // style classes
  containerClassName: 'adk-toast__container',
  toastClassName: 'adk-toast__toast',
  bodyClassName: 'adk-toast__body'
} as ToastContainerOptions)
app.use(i18nInstance, { router })
app.use(router)
app.use(VueDOMPurifyHTML)
app.use(pinia)
app.use(ConfirmDialog)

app.mount('#app')
